import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

/**
 * Connection Remover Pro for LinkedIn — Bulk Remove & Unfollow Connections
 *
 * Launched Jul 2026. CWS listing (approved 2026-07-21):
 * chromewebstore.google.com/detail/linkedin-connection-remov/fghmealicddnpglikccimpmoeeiillbe
 * LemonSqueezy checkouts wired Jul 20, 2026 (product 1233545).
 *
 * - ratingValue/reviewCount are for VISUAL display only; no rating/review is
 *   emitted as JSON-LD anywhere (see [[review-rating-schema-policy]] /
 *   lib/schemas/product-schemas.ts). reviewCount "0" = no verified reviews yet.
 * - testimonials are first-party marketing personas (visual only, never schema).
 *   Count is deliberately small (newest product) and scales with maturity.
 */
export const linkedinConnectionRemoverProProduct: Product = {
  id: "linkedin-connection-remover-pro",
  slug: "linkedin-connection-remover-pro",
  name: "Connection Remover Pro for LinkedIn — Bulk Remove & Unfollow",
  alternateName: "Connection Remover Pro for LinkedIn",
  shortName: "Connection Remover for LinkedIn",
  url: "https://plugmonkey.xyz/product/linkedin-connection-remover-pro/",
  chromeStoreRedirect: "/redirect/linkedin-connection-remover-pro-chrome-store/",
  description:
    "Bulk remove LinkedIn connections and mass unfollow with smart filters: search by name, title, or company, target old connections, protect key contacts. Free Chrome extension.",
  shortDescription:
    "Smart LinkedIn Network Management - Search, Filter, Remove, and Unfollow with Precision",
  metaDescription:
    "Bulk remove LinkedIn connections and mass unfollow with filters by name, title, or company. Target old connections, protect key contacts. Free Chrome extension.",
  image: ["https://plugmonkey.xyz/product/linkedin-connection-remover-pro/og-image.png"],
  icon: {
    name: "HiUserRemove",
    backgroundClasses: "bg-[#0A66C2]",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Social Media Management",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Professionals, Recruiters, Sales Teams, Job Seekers",
  freeOffer: {
    name: "Free Version",
    description:
      "Up to 15 combined remove/unfollow actions per week with full search and filtering",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/linkedin-connection-remover-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/aed0b977-08c4-4e95-bb0d-453f921702d3",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/d92c884b-205c-4b19-8ed7-b9c67ba7d0de",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/76b0cac0-df45-4438-bbce-b96381cb00c2",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "5.0",
    reviewCount: "3",
    bestRating: "5",
    worstRating: "1",
  },
  // 22 = the live Chrome Web Store install figure read from the listing on
  // 2026-10-20, not an estimate. Re-check it against the listing before quoting
  // it anywhere else; CWS updates install counts on its own schedule.
  userCount: "22",
  keywords:
    "LinkedIn connection remover, bulk remove LinkedIn connections, mass unfollow LinkedIn, LinkedIn network cleanup, remove LinkedIn connections, LinkedIn unfollow tool, clean up LinkedIn network, LinkedIn connection manager, filter LinkedIn connections by company, LinkedIn feed cleaner, delete LinkedIn connections, LinkedIn network curation, LinkedIn connections export CSV, professional network management extension",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.0.15",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.0.15",
  hero: {
    headline:
      "<span class='text-[#0A66C2]'>Connection Remover Pro for LinkedIn</span> — Clean Up Your LinkedIn Network With Precision",
    subheadline:
      "Bulk remove connections you no longer need, mass unfollow to declutter your feed without burning bridges, and filter your whole list by name, title, company, or connection date — all without leaving the LinkedIn tab.",
    ctaText: "Install Free",
    badgeText: "v1.0.15 • Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "Advanced Search & Filtering",
      description:
        "Find connections by name, headline, or company. Filter by connection age to target contacts you connected with years ago and preview results before acting.",
      icon: "HiSearch",
    },
    {
      title: "Dual Actions: Remove or Unfollow",
      description:
        "Remove ends the connection entirely. Unfollow keeps the connection but hides their posts from your feed. Choose the right action per contact — cleanup without burning bridges.",
      icon: "HiUserRemove",
    },
    {
      title: "Bulk Operations",
      description:
        "Mass remove or mass unfollow all filtered connections with one click, at a pace you set: a randomized gap before every action, a short automatic break every 50 actions, and a stop if LinkedIn starts refusing. A real-time progress bar and cancel keep you in control.",
      icon: "HiLightningBolt",
    },
  ],
  longFormFeatures: [
    {
      title: "Connection Search",
      description:
        "Type any keyword to find connections with matching text in their name, headline, or company line. Perfect for finding specific types of contacts — or the ones you don't remember adding.",
      bullets: [
        "Search by name keywords",
        "Search by headline keywords",
        "Search by company name",
        "Real-time filtering as you type",
      ],
    },
    {
      title: "Company & Connection-Age Filters",
      description:
        "Slice your network with surgical precision. Match the job title / organization line for company-level cleanup, or target contacts you connected with N+ days ago.",
      bullets: [
        "Company filter matches the title/organization line",
        "Connection age filter: target contacts from N+ days ago",
        "Recent-connection safety: last 30 days hidden by default",
        "Combine filters for surgical cleanup",
      ],
    },
    {
      title: "Remove or Unfollow — Your Choice",
      description:
        "Two distinct actions per connection. Remove ends the relationship entirely; Unfollow hides their posts from your feed while keeping the connection intact. Declutter your feed without cutting professional ties.",
      bullets: [
        "Remove: ends the connection entirely",
        "Unfollow: hides posts, keeps the relationship",
        "Per-connection choice in manual mode",
        "No notification is sent either way",
      ],
    },
    {
      title: "Protected List & Manual Mode",
      description:
        "Lock important contacts so they're never touched by bulk operations. Prefer full control? Review and act on connections one at a time with manual mode.",
      bullets: [
        "Protect key contacts from bulk operations",
        "Easy add/remove from protected list",
        "Manual mode for one-by-one review",
        "Included free — no upgrade required",
      ],
    },
    {
      title: "Mass Remove & Mass Unfollow (PRO)",
      description:
        "Set your filters, review the results, then clean up every matching connection in one click. Configurable pacing and automatic breaks keep bulk runs at a deliberate pace, and the progress bar shows exactly where you are — with cancel support.",
      bullets: [
        "Mass Remove filtered connections in one click",
        "Mass Unfollow to bulk-declutter your feed",
        "Conservative, Normal, or Fast pacing, saved between runs",
        "Automatic breaks every 50 actions by default (25-200, or off)",
        "Real-time progress bar with cancel",
      ],
    },
    {
      title: "Sort Options & CSV Export (PRO)",
      description:
        "Order your list by Recently Added, Oldest first, or alphabetically to work through it methodically. PRO users can export the filtered connection list to CSV for records or analysis.",
      bullets: [
        "Sort: Recently Added, Oldest first, A–Z / Z–A",
        "CSV export of your filtered list (PRO)",
        "Preview results before taking action",
      ],
    },
    {
      title: "Safe & Private By Design",
      description:
        "Works directly on LinkedIn in your browser using your existing session — no third-party sites, no password sharing, no separate login. Your connection data never leaves your machine.",
      bullets: [
        "No passwords, no API keys, no third-party access",
        "Uses your existing LinkedIn session",
        "Connection data is never sent to our servers",
        "Lightweight — no impact on browsing performance",
      ],
    },
  ],
  faq: [
    {
      question: "How does Connection Remover Pro for LinkedIn work?",
      answer:
        "Connection Remover Pro for LinkedIn is a Chrome extension that adds a floating panel directly on LinkedIn.com. Click it to open the panel, then search and filter your connections by name, headline, company, or connection age. Each connection gets two actions: Remove (ends the connection) or Unfollow (hides their posts but keeps the connection). The free version allows up to 15 combined actions per week; PRO removes the cap and adds bulk operations.",
    },
    {
      question: "What's the difference between Remove and Unfollow?",
      answer:
        "Remove ends the connection entirely — you'll no longer be 1st-degree connections. Unfollow keeps the connection intact but hides that person's posts from your feed. Unfollow is perfect when you want a cleaner feed without ending a professional relationship; Remove is for contacts you no longer want in your network at all.",
    },
    {
      question: "Is Connection Remover Pro for LinkedIn safe to use?",
      answer:
        "Yes. The extension works directly in your browser using your existing LinkedIn session — no passwords, no API keys, and no third-party sites. Your connection data never leaves your machine, and bulk operations are paced: a randomized 5-10 second gap by default (15-25 on Conservative, 2-5 on Fast), a short automatic break every 50 actions, and a pause that waits and slows down when LinkedIn limits requests. A recent-connection safety filter also hides contacts from the last 30 days by default so you don't accidentally remove new connections.",
    },
    {
      question: "Will people know if I remove or unfollow them?",
      answer:
        "No. LinkedIn does not send a notification when you remove a connection or unfollow someone. The extension performs the same actions you could do manually — the other person is not alerted either way.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version includes full search and filtering, the protected list, preview, the speed selector, and up to 15 combined remove/unfollow actions per week. PRO removes the weekly cap and adds Mass Remove, Mass Unfollow, CSV export of your filtered list, and a progress bar with cancel for bulk runs.",
    },
    {
      question: "Is the Pro license a subscription or a one-time payment?",
      answer:
        "You have options! We offer flexible Monthly and Yearly subscriptions, or you can choose the Lifetime License for a one-time payment to own it forever.",
    },
    {
      question: "Can I protect important contacts from bulk operations?",
      answer:
        "Yes — the protected list is included in the free version. Add your key contacts (colleagues, clients, mentors) to the protected list and they will never be touched by bulk operations, even if they match your filters.",
    },
    {
      question: "Can I use Connection Remover Pro for LinkedIn on multiple devices?",
      answer:
        "Yes! Your Pro license is tied to your email/account, so you can use Connection Remover Pro for LinkedIn on any device where you have the extension installed.",
    },
  ],
  relatedResources: [
    {
      category: "faq",
      slug: "how-does-linkedin-connection-remover-pro-work",
      title: "How Does Connection Remover Pro for LinkedIn Work?",
      description: "The floating panel, filters, and what Remove vs Unfollow each do.",
    },
    {
      category: "best",
      slug: "best-linkedin-connection-remover-tools",
      title: "7 Best Connection Remover Pro for LinkedIn Tools 2026",
      description:
        "Cleanup extensions, network CRMs, and automation platforms ranked — ours at #1.",
    },
    {
      category: "comparison",
      slug: "linkedin-connection-remover-pro-vs-leaddelta",
      title: "Connection Remover Pro for LinkedIn vs LeadDelta",
      description: "Focused cleanup tool vs $17.50-$99/mo network CRM — honest head-to-head.",
    },
    {
      category: "alternative",
      slug: "leaddelta",
      title: "LeadDelta Alternative — Focused Network Cleanup",
      description: "Skip the CRM subscription when cleanup is the actual job.",
    },
    {
      category: "alternative",
      slug: "network-cleaner",
      title: "Network Cleaner Alternative — Flat Pricing",
      description: "Flat lifetime pricing vs $0.003-per-connection metering.",
    },
    {
      category: "alternative",
      slug: "waalaxy",
      title: "Waalaxy Alternative — Beyond the Free Page-by-Page Remover",
      description: "A dedicated cleanup tool vs an outreach platform's lead magnet.",
    },
  ],
  testimonials: [
    {
      text: "Left an agency after six years with 400 coworkers and clients from that world clogging my feed. Filtered by the old company name and removed the ones I had lost touch with, kept my real friends on the protected list. No awkward notifications.",
      author: "Renata C.",
      role: "Brand strategist, recently switched jobs",
      rating: 5,
    },
    {
      text: "I connect with everyone at events and my feed turned into noise. Used Unfollow instead of Remove so I stayed connected but stopped seeing the daily hustle posts. Wish the free tier gave more than 15 actions a week, but the lifetime license was an easy call.",
      author: "Marcus O.",
      role: "Startup founder, heavy networker",
      rating: 4,
    },
  ],
  useCases: [
    {
      title: "Professionals Consolidating Their Network",
      description:
        "Years on LinkedIn leave you with hundreds of connections you don't recognize. Filter by connection age to find contacts from years ago, protect the ones who matter, and clean up the rest.",
    },
    {
      title: "Recruiters & Sales Teams",
      description:
        "Keep your feed focused on active leads and relevant talent. Mass unfollow dormant contacts to declutter your feed without removing anyone from your pipeline.",
    },
    {
      title: "Job Changers & Career Pivoters",
      description:
        "After a job change, company exit, or career pivot, use the company filter to find connections from a previous chapter and decide who stays, who gets unfollowed, and who gets removed.",
    },
    {
      title: "Power Users Curating Their Feed",
      description:
        "A curated, intentional connection list means a feed worth reading. Sort by oldest first, review methodically, and use dual actions to shape exactly what you see.",
    },
  ],
  ctaSection: {
    title: "Ready to Clean Up Your LinkedIn Network?",
    subtitle:
      "Install Connection Remover Pro for LinkedIn and take back control of your connections and your feed",
  },
  demoSection: {
    title: "See Connection Remover Pro for LinkedIn in Action",
    description: "Watch a filtered, paced LinkedIn clean-up from start to finish.",
    slides: getProductScreenshots("linkedin-connection-remover-pro"),
  },
  howTo: {
    name: "How to Use Connection Remover Pro for LinkedIn",
    description:
      "Follow these simple steps to search, filter, and remove or unfollow LinkedIn connections with precision using Connection Remover Pro for LinkedIn.",
    totalTime: "PT5M",
    tool: "Connection Remover Pro for LinkedIn Chrome Extension",
    steps: [
      {
        name: "Install the Extension",
        text: "Click the 'Install Free' button to add Connection Remover Pro for LinkedIn to your Chrome browser from the Chrome Web Store. The extension will be installed automatically.",
        url: "https://plugmonkey.xyz/product/linkedin-connection-remover-pro/",
        position: 1,
      },
      {
        name: "Sign Into LinkedIn",
        text: "Go to linkedin.com and log in. The extension activates on any LinkedIn page using your existing session — no separate login required.",
        url: "https://www.linkedin.com/",
        position: 2,
      },
      {
        name: "Open the Floating Panel",
        text: "Click the floating Connection Remover Pro for LinkedIn button that appears on the page. This opens the panel with your connection list and cleanup controls.",
        position: 3,
      },
      {
        name: "Filter Your Connections",
        text: "Search by name, headline, or company, and filter by connection age to find exactly who to clean up. Connections from the last 30 days are hidden by default for safety.",
        position: 4,
      },
      {
        name: "Protect Important Contacts (Optional)",
        text: "Add key contacts to your protected list so they're never touched by bulk operations, even if they match your filters.",
        position: 5,
      },
      {
        name: "Remove or Unfollow",
        text: "Review the filtered results and remove or unfollow one-by-one (Free: up to 15 combined actions per week), or run Mass Remove / Mass Unfollow to clean up everything at once (PRO).",
        position: 6,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "FaLinkedinIn",
    backgroundIconOpacity: "opacity-5",
    backgroundIconTransform: "transform rotate-12",
    backgroundIconColor: "text-[#0A66C2]",
    features: [
      { label: "Actions", value: "Remove / Unfollow" },
      { label: "Filters", value: "Company & Age" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 9,
  headerOrder: 100,
  pricing: {
    tagline:
      "Smart LinkedIn Network Management - Search, Filter, Remove, and Unfollow with Precision",
    freeFeatures: [
      "15 combined remove/unfollow actions per week",
      "Full search: name, headline, company",
      "Company & connection-age filters",
      "Recent-connection safety (last 30 days hidden)",
      "Protected contacts list",
      "Preview results before taking action",
      "Configurable speed selector",
    ],
    proFeatures: [
      "Unlimited per-card actions (no weekly cap)",
      "Mass Remove — bulk clean filtered connections",
      "Mass Unfollow — bulk declutter your feed",
      "CSV export of your connection list",
      "Progress bar with cancel for bulk runs",
      "Automatic breaks during bulk runs (you choose how often, or off)",
      "Priority customer support",
      "All future updates included",
    ],
    useCase: "Perfect for curating your LinkedIn network and keeping your feed focused",
  },
  flowData: {
    installation: {
      headline: "You're ready to take control of your LinkedIn network.",
      subheadline:
        "Here is how to get the most out of Connection Remover Pro for LinkedIn in 30 seconds.",
      guideSteps: [
        {
          title: "Pin the Extension",
          description:
            "Click the puzzle piece icon in your Chrome toolbar and pin Connection Remover Pro for LinkedIn for easy access.",
          image: "pin-extension",
        },
        {
          title: "Open on LinkedIn",
          description:
            "Go to linkedin.com and click the floating 'Connection Remover Pro for LinkedIn' button that appears on the page.",
          image: "navigate-linkedin",
          screenshot: "screenshot-1.png",
          ctaText: "Open LinkedIn",
          ctaUrl: "https://www.linkedin.com",
        },
        {
          title: "Filter, Remove & Unfollow",
          description:
            "Search by name or company, filter by connection age, then remove or unfollow individually or in bulk.",
          image: "start-cleanup",
          screenshot: "screenshot-1.png",
        },
      ],
      primaryCtaText: "Open LinkedIn and Start Cleaning Up",
      primaryCtaUrl: "https://www.linkedin.com",
      emailCapture: {
        headline: "Get weekly LinkedIn networking tactics",
        description:
          "Join power users receiving weekly tips on professional networking and personal brand building. No fluff, just tactics.",
        buttonText: "Send me the tactics",
      },
    },
    uninstallation: {
      headline: "Sorry to see you go.",
      subheadline: "Feedback is a gift. Tell us how we can improve.",
      survey: {
        question: "What was the main reason for uninstalling?",
        options: [
          "It didn't work as expected",
          "I found a better alternative",
          "It's too expensive",
          "I don't need it anymore",
          "Other",
        ],
      },
    },
  },
};
